export const DEFAULT_DEPTH_BUCKET_COUNT = 65536;

const MIN_DEPTH_RANGE = 1e-6;

export function createDepthSortBuffers(
  count,
  bucketCount = DEFAULT_DEPTH_BUCKET_COUNT,
) {
  if (!Number.isInteger(count) || count <= 0) {
    throw new Error(`Invalid Gaussian sort count: ${count}`);
  }

  if (!Number.isInteger(bucketCount) || bucketCount <= 0) {
    throw new Error(`Invalid Gaussian depth bucket count: ${bucketCount}`);
  }

  return {
    depths: new Float32Array(count),
    buckets: new Uint16Array(count),
    counts: new Uint32Array(bucketCount),
    offsets: new Uint32Array(bucketCount),
    sortedIndices: new Float32Array(count),
  };
}

function writeSequentialIndices(batches) {
  let index = 0;

  for (const batch of batches) {
    const attribute = batch.geometry.getAttribute("gaussianIndex");

    for (let i = 0; i < batch.count; i += 1) {
      attribute.array[i] = index;
      index += 1;
    }

    attribute.needsUpdate = true;
  }
}

function writeSortedIndices(batches, sortedIndices) {
  let offset = 0;

  for (const batch of batches) {
    const attribute = batch.geometry.getAttribute("gaussianIndex");

    attribute.array.set(sortedIndices.subarray(offset, offset + batch.count));

    attribute.needsUpdate = true;
    offset += batch.count;
  }
}

export function sortGaussiansByDepth({
  positions,
  count,
  camera,
  batches,
  buffers,
  bucketCount = DEFAULT_DEPTH_BUCKET_COUNT,
}) {
  if (!positions || !camera || !buffers || !batches?.length || count <= 0) {
    return null;
  }

  if (count > buffers.depths.length) {
    throw new Error("Gaussian sort count exceeds allocated sort buffers.");
  }

  if (bucketCount > buffers.counts.length) {
    throw new Error("Gaussian bucket count exceeds allocated sort buffers.");
  }

  const startTime = performance.now();

  camera.updateMatrixWorld(true);

  const view = camera.matrixWorldInverse.elements;
  const { depths, buckets, counts, offsets, sortedIndices } = buffers;

  let minDepth = Infinity;
  let maxDepth = -Infinity;

  // Compute camera-space depth and its range.
  for (let i = 0; i < count; i += 1) {
    const offset = i * 3;
    const x = positions[offset];
    const y = positions[offset + 1];
    const z = positions[offset + 2];

    const cameraZ = view[2] * x + view[6] * y + view[10] * z + view[14];

    const depth = -cameraZ;

    depths[i] = depth;

    if (depth < minDepth) {
      minDepth = depth;
    }

    if (depth > maxDepth) {
      maxDepth = depth;
    }
  }

  const depthRange = maxDepth - minDepth;

  if (!Number.isFinite(depthRange) || depthRange < MIN_DEPTH_RANGE) {
    writeSequentialIndices(batches);

    return {
      milliseconds: performance.now() - startTime,
      minimumDepth: minDepth,
      maximumDepth: maxDepth,
      degenerate: true,
    };
  }

  counts.fill(0);

  const bucketScale = (bucketCount - 1) / depthRange;

  // Quantize each Gaussian into a depth bucket.
  for (let i = 0; i < count; i += 1) {
    const normalizedDepth = (depths[i] - minDepth) * bucketScale;

    const bucket = Math.max(
      0,
      Math.min(bucketCount - 1, Math.floor(normalizedDepth)),
    );

    buckets[i] = bucket;
    counts[bucket] += 1;
  }

  // Prefix offsets run from far to near for alpha compositing.
  let outputOffset = 0;

  for (let bucket = bucketCount - 1; bucket >= 0; bucket -= 1) {
    offsets[bucket] = outputOffset;
    outputOffset += counts[bucket];
  }

  counts.fill(0);

  for (let i = 0; i < count; i += 1) {
    const bucket = buckets[i];
    const destination = offsets[bucket] + counts[bucket];

    sortedIndices[destination] = i;
    counts[bucket] += 1;
  }

  writeSortedIndices(batches, sortedIndices);

  return {
    milliseconds: performance.now() - startTime,
    minimumDepth: minDepth,
    maximumDepth: maxDepth,
    degenerate: false,
  };
}
